import { Big } from "big.js";
import { Currency, getCurrencyMinorUnits, MinorUnits } from "./currencies";

// export type MoneyValue<C extends Currency> = BaseMoneyValue<MinorUnits<C>>;
// We have the base type so we can hide the minor units in the exported type
interface BaseMoneyValue<T extends number> {
  readonly amount: Big;
  readonly minorUnits: T;
  readonly name: string;
  readonly operation?: Operation;
}
export type MoneyValue<C extends Currency> = BaseMoneyValue<MinorUnits<C>>;

enum OperationType {
  SUM = "sum",
  SUBTRACT = "subtract",
  MULTIPLY = "multiply",
  DISTRIBUTE = "distribute",
  OFFSET = "offset",
}

interface BaseOperation {
  readonly type: OperationType;
  readonly reason: string;
}

interface TwoValueOperation extends BaseOperation {
  readonly primaryValue: BaseMoneyValue<number>;
  readonly secondaryValue: BaseMoneyValue<number>;
  readonly type: OperationType.SUBTRACT | OperationType.OFFSET;
}

interface MultiValueOperation extends BaseOperation {
  readonly values: BaseMoneyValue<number>[];
  readonly type: OperationType.SUM;
}

interface MultiplyOperation extends BaseOperation {
  readonly value: BaseMoneyValue<number>;
  readonly scalar: Big;
  readonly type: OperationType.MULTIPLY;
}

interface DistributionOperation extends BaseOperation {
  readonly value: BaseMoneyValue<number>;
  readonly proportions: Big[];
  readonly proportionIndex: number;
  readonly type: OperationType.DISTRIBUTE;
}

type Operation =
  | TwoValueOperation
  | MultiValueOperation
  | MultiplyOperation
  | DistributionOperation;

export const moneyValueToString = (moneyValue: MoneyValue<Currency>) => {
  return moneyValue.amount.toFixed(moneyValue.minorUnits);
};

export const moneyValueToNumber = (moneyValue: MoneyValue<Currency>) => {
  return moneyValue.amount.toNumber();
};

export namespace MoneyCalculator {
  export class InvalidProportionsError extends Error {
    override name = "InvalidProportionsError";
  }
  export class InvalidMinorUnitsError extends Error {
    override name = "InvalidMinorUnitsError";
  }

  export function newMoneyValue<C extends Currency>({
    amount,
    name,
    currency,
  }: {
    amount: Big;
    name: string;
    currency: C;
  }): BaseMoneyValue<MinorUnits<C>> {
    const minorUnits = getCurrencyMinorUnits(currency);
    return {
      amount: amount.round(minorUnits),
      name,
      minorUnits,
    };
  }

  export function sum<C extends Currency>({
    values,
    reason,
    newValueName,
    currency,
  }: {
    values: MoneyValue<C>[];
    reason: string;
    newValueName: string;
    currency: C;
  }): MoneyValue<C> {
    return {
      amount: values.reduce((acc, value) => acc.plus(value.amount), Big(0)),
      operation: { type: OperationType.SUM, reason, values },
      name: newValueName,
      minorUnits: getCurrencyMinorUnits(currency),
    };
  }

  export function subtract<C extends Currency>({
    primaryValue,
    secondaryValue,
    reason,
    newValueName,
  }: {
    primaryValue: MoneyValue<C>;
    secondaryValue: MoneyValue<C>;
    reason: string;
    newValueName: string;
  }): MoneyValue<C> {
    return {
      amount: primaryValue.amount.minus(secondaryValue.amount),
      operation: {
        type: OperationType.SUBTRACT,
        reason,
        primaryValue,
        secondaryValue,
      },
      name: newValueName,
      minorUnits: primaryValue.minorUnits,
    };
  }

  /** Do NOT use this function for cases where the distribute function could be used instead. */
  export function multiply<T extends number>({
    value,
    scalar,
    reason,
    newValueName,
  }: {
    value: BaseMoneyValue<T>;
    scalar: Big;
    reason: string;
    newValueName: string;
  }): BaseMoneyValue<T> {
    return {
      amount: value.amount.times(scalar).round(value.minorUnits),
      operation: { type: OperationType.MULTIPLY, reason, value, scalar },
      name: newValueName,
      minorUnits: value.minorUnits,
    };
  }

  const _distribute = ({
    amountToDistribute,
    proportions,
    minorUnits,
  }: {
    amountToDistribute: Big;
    proportions: Big[];
    minorUnits: number;
  }): Big[] => {
    if (proportions[0] === undefined) {
      return [];
    }

    const sumOfProportions = proportions.reduce(
      (acc, proportion) => acc.plus(proportion),
      new Big(0),
    );
    let amount;
    if (sumOfProportions.eq(0)) {
      amount = new Big(0);
    } else {
      amount = amountToDistribute
        .times(proportions[0])
        .div(sumOfProportions)
        .round(minorUnits, Big.roundUp);
    }

    return [
      amount,
      ..._distribute({
        amountToDistribute: amountToDistribute.minus(amount),
        proportions: proportions.slice(1),
        minorUnits,
      }),
    ];
  };

  const _validateProportions = (
    proportions: Big[],
    valueToDistribute: BaseMoneyValue<number>,
  ) => {
    if (proportions.some((proportion) => proportion.lt(0))) {
      throw new InvalidProportionsError("Proportions must not be negative");
    }
    if (valueToDistribute.amount.gt(0) && proportions.length === 0) {
      throw new InvalidProportionsError(
        "Unable to distribute amount: No proportions provided",
      );
    }
  };

  export function distribute<T extends number>({
    valueToDistribute,
    proportions: _proportions,
    reason,
    newValueName,
  }: {
    valueToDistribute: BaseMoneyValue<T>;
    proportions: Big[];
    reason: string;
    newValueName: string;
  }): BaseMoneyValue<T>[] {
    let proportions = _proportions;
    // If all proportions are 0, set them all to 1
    // so we can distribute the value evenly
    if (proportions.every((proportion) => proportion.eq(0))) {
      proportions = _proportions.map(() => Big(1));
    }
    _validateProportions(proportions, valueToDistribute);
    const distributedAmounts = _distribute({
      amountToDistribute: valueToDistribute.amount,
      proportions,
      minorUnits: valueToDistribute.minorUnits,
    });
    return distributedAmounts.map((amount, index) => ({
      amount,
      operation: {
        type: OperationType.DISTRIBUTE,
        reason,
        value: valueToDistribute,
        proportions,
        proportionIndex: index,
      },
      name: newValueName,
      minorUnits: valueToDistribute.minorUnits,
    }));
  }

  /**
   * Offsets two MoneyValues by subtracting the smaller from the larger.
   * One side is fully zeroed out, and the other holds the remainder.
   *
   * Example:
   *   x = $100, y = $60 → { x: $40, y: $0 }
   *   x = $40, y = $100 → { x: $0, y: $60 }
   */
  export function offsetAmounts<C extends Currency>({
    primaryValue,
    secondaryValue,
    reason,
    newValueNames,
  }: {
    primaryValue: MoneyValue<C>;
    secondaryValue: MoneyValue<C>;
    reason: string;
    newValueNames: { primaryValue: string; secondaryValue: string };
  }): { primaryValue: MoneyValue<C>; secondaryValue: MoneyValue<C> } {
    const operation = {
      type: OperationType.OFFSET,
      reason,
      primaryValue,
      secondaryValue,
    } as const;

    const primaryValueGreater = primaryValue.amount.gt(secondaryValue.amount);

    return {
      primaryValue: {
        amount: primaryValueGreater
          ? primaryValue.amount.minus(secondaryValue.amount)
          : Big(0),
        name: newValueNames.primaryValue,
        minorUnits: primaryValue.minorUnits,
        operation,
      },
      secondaryValue: {
        amount: primaryValueGreater
          ? Big(0)
          : secondaryValue.amount.minus(primaryValue.amount),
        name: newValueNames.secondaryValue,
        minorUnits: secondaryValue.minorUnits,
        operation,
      },
    };
  }
}
